import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, DestroyRef, effect, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule, MatOptionSelectionChange } from '@angular/material/core';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule, MatSelectTrigger } from '@angular/material/select';
import { ICampaignForm } from '@campaign-form/campaign-form.interface';
import { CampaignFormStore } from '@campaign-form/campaign-form.store';
import { ITeam } from '@core/interfaces/team.interface';
import { PermissionService } from '@core/services/permission.service';
import { EPermissions } from '@enums/permissions.enum';
import { TranslateModule } from '@ngx-translate/core';
import { AddTeamDialogComponent } from '@pages/settings/user-management/users-tab/add-team-dialog/add-team-dialog.component';
import { IconBadgeComponent } from '@shared/components/icon-badge/icon-badge.component';
import { IconComponent } from '@shared/components/icon/icon.component';
import { uniqBy } from 'lodash';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

/**
 * The campaign's own organizational "Team" field - a single-select, "Add team inline" mat-select
 * mirroring CampaignCustomerComponent's ("Client") structure, but for `ICampaignForm.team`/
 * `teamId` rather than `customer`/`customerId`. Unrelated to CampaignTeamComponent (the
 * "Visibility" section below it in the Basics form) - that one controls *who can see* the
 * campaign (a whitelist that can include whole teams, see that component's own doc comment); this
 * one assigns the campaign *to* one team, purely so the campaigns list can filter by it (see
 * CampaignsDatatableComponent's "Team" filter).
 *
 * Visibility of the field itself is permission-gated: reading the team list needs
 * `EPermissions.teamReadOwn` (without it, teams are never fetched and the field simply doesn't
 * render - there would be nothing to select and no way to know what's already assigned), and the
 * inline "Add team" option needs `EPermissions.teamCreate`. If there are no teams yet and the
 * current user can't create one either, the whole field is hidden - there would be nothing useful
 * to do with it.
 */
@Component({
  imports: [
    IconBadgeComponent,
    IconComponent,
    MatButtonModule,
    MatFormFieldModule,
    MatOptionModule,
    MatSelectModule,
    MatSelectTrigger,
    NgTemplateOutlet,
    NgxMatSelectSearchModule,
    NgxSkeletonLoaderModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  selector: 'app-campaign-team-select',
  templateUrl: './campaign-team-select.component.html',
})
export class CampaignTeamSelectComponent {
  public readonly form = input.required<FormGroup<ICampaignForm>>();

  public readonly newTeams = signal<ITeam[]>([]);
  public readonly searchTerm = signal<string>('');
  public readonly selectedTeam = signal<ITeam | null>(null);

  public readonly teamSearchControl = new FormControl<string | null>('');

  public readonly campaignFormStore = inject(CampaignFormStore);
  private readonly permissionService = inject(PermissionService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  private teamControlSubscription: Subscription | null = null;

  private readonly currentUserPermissions = toSignal(this.permissionService.currentUserPermissions$, {
    initialValue: this.permissionService.currentUserPermissions,
  });

  public readonly canReadTeams = computed(() => this.currentUserPermissions().includes(EPermissions.teamReadOwn));
  public readonly canCreateTeam = computed(() => this.currentUserPermissions().includes(EPermissions.teamCreate));

  public readonly teams = computed<ITeam[]>(() => {
    const uniqueTeams = uniqBy(
      [...(this.newTeams()?.length ? this.newTeams() : []), ...(this.campaignFormStore.teams$() ?? [])],
      'id',
    );

    return uniqueTeams.sort((a: ITeam, b: ITeam) => a.name.localeCompare(b.name));
  });

  /** True while the initial fetch is still outstanding - distinct from `canReadTeams() === false`
   * (never fetched at all, field just won't render) and from an already-resolved empty list
   * (`teams$()` is `[]`, not `null`). */
  public readonly loading = computed<boolean>(() => this.canReadTeams() && this.campaignFormStore.teams$() === null);

  public readonly filteredTeams = computed(() => {
    const term: string = this.searchTerm().toLowerCase();
    const selectedTeam: ITeam | null = this.selectedTeam();

    let filtered = term ? this.teams().filter((team: ITeam) => team.name.toLowerCase().includes(term)) : this.teams();

    if (selectedTeam) {
      const selectedIndex = filtered.findIndex((team: ITeam) => team.id === selectedTeam.id);

      if (selectedIndex > -1) {
        filtered = [filtered[selectedIndex], ...filtered.filter((team: ITeam) => team.id !== selectedTeam.id)];
      } else {
        filtered = [selectedTeam, ...filtered];
      }
    }

    return filtered;
  });

  constructor() {
    this.createTeamFillEffect();
    this.subscribeToTeamChanges();
    this.subscribeToSearchChanges();
  }

  /** Mirrors the "team" control's value except while it's transiently `undefined` - which only
   * happens the instant the "Add team" trigger option (a `mat-option` with no `[value]`) gets
   * selected. That selection fires regardless of HOW the option is activated: a mouse click runs
   * MatOption's own `(click)="_selectViaInteraction()"` host listener, but keyboard Enter/Space
   * goes through its `(keydown)` listener instead, calling `_selectViaInteraction()` directly
   * without ever dispatching a real DOM click event - so a `(click)`/`(mousedown)` binding on the
   * option (an earlier version of this snapshot used exactly that) never even fires for keyboard
   * activation, silently clearing the selection with no way to restore it. Driving this off
   * `valueChanges` instead (see subscribeToTeamChanges) works no matter how "Add team" was
   * reached. Only updated for a real team or an explicit Clear (`null`) - see
   * subscribeToTeamChanges - so it always holds the last *meaningful* selection, never the
   * "Add team" trigger's own transient `undefined`. */
  private lastKnownTeam: ITeam | null = null;

  /** `(click)` alone would miss keyboard activation (see lastKnownTeam's doc comment), but
   * `onSelectionChange` alone isn't safe either - MatOption emits it any time MatSelect
   * resyncs its selection model against a programmatic value change (e.g. this component's own
   * `fillTeam` patching `team` to a freshly-resolved object once `teams()` loads), not just on a
   * genuine user click/keypress. `isUserInput` is what actually distinguishes the two - only true
   * when a real interaction (mouse or keyboard) drove the selection. */
  public onAddTeamOptionSelected(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }

    this.openTeamCreateDialog();
  }

  /** Same `isUserInput` reasoning as onAddTeamOptionSelected above. */
  public onClearTeamOptionSelected(event: MatOptionSelectionChange): void {
    if (!event.isUserInput) {
      return;
    }

    this.clearTeam();
  }

  public openTeamCreateDialog(): void {
    const dialogRef = this.dialog.open<AddTeamDialogComponent, undefined, ITeam | undefined>(AddTeamDialogComponent, {
      disableClose: true,
      minWidth: '700px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((team: ITeam | undefined) => {
        if (!team) {
          const previousTeam = this.lastKnownTeam;

          this.form()?.patchValue({ team: previousTeam, teamId: previousTeam?.id ?? null });
          this.selectedTeam.set(previousTeam);

          return;
        }

        this.newTeams.set([team, ...this.newTeams()]);
        this.campaignFormStore.upsertTeam(team);
        this.form()?.patchValue({ team, teamId: team.id });
      });
  }

  /** Explicit "no team" option - a plain `mat-select` otherwise has no way to deselect a chosen
   * team once one is picked (`team`/`teamId` are optional, unlike e.g. `customer`, so clearing
   * back to "no team" needs to be possible). */
  public clearTeam(): void {
    this.form()?.patchValue({ team: null, teamId: null });
    this.selectedTeam.set(null);
  }

  private createTeamFillEffect(): void {
    effect(() => {
      this.fillTeam();
    });
  }

  private subscribeToSearchChanges(): void {
    this.teamSearchControl.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchValue: string | null) => {
        this.searchTerm.set(searchValue ?? '');
      });
  }

  private subscribeToTeamChanges(): void {
    effect(() => {
      const teamControl = this.form()?.get('team');

      if (teamControl) {
        const initialTeam: ITeam | null = teamControl.value ?? null;
        this.selectedTeam.set(initialTeam);
        this.lastKnownTeam = initialTeam;

        this.teamControlSubscription?.unsubscribe();
        this.teamControlSubscription = teamControl.valueChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((team: ITeam | null | undefined) => {
            this.selectedTeam.set(team ?? null);

            // Skip the "Add team" trigger's own transient `undefined` - see lastKnownTeam's doc
            // comment. A real team or an explicit Clear (`null`) both count as a meaningful
            // selection worth remembering.
            if (team !== undefined) {
              this.lastKnownTeam = team;
            }

            // Keeps `teamId` in sync with every selection, not just openTeamCreateDialog's own
            // explicit patch - previously, picking an *existing* team from the dropdown left
            // `teamId` on whatever value it last held (null, or a stale/different team's id),
            // desynced from `team`. `campaign-form-payload.service.ts` itself only ever reads
            // `team?.id` for the submit payload, so this didn't corrupt what got saved, but the
            // two controls disagreeing is still a latent trap for anything reading `teamId`
            // directly (or a future payload change that switches to it).
            this.form()
              ?.get('teamId')
              ?.setValue(team?.id ?? null);
          });
      }
    });
  }

  /** Resolves the form's `teamId` (set from outside this component, e.g. the campaign's own
   * saved value) into the actual `ITeam` object the rest of this component works with. `teams()`
   * is capped at 1000 fetched teams (see CampaignFormStore.fetchTeams), so a `teamId` that isn't
   * in that list doesn't necessarily mean the assignment is gone - if the `team` control already
   * holds a matching object (patched in directly from the joined campaign response, see
   * CampaignBasicsComponent's form init), that's preserved instead of being clobbered with
   * `undefined`, which previously cascaded through the `team` control's own subscription into
   * clearing `teamId` right back to `null` on any unrelated save. Only when neither source can
   * resolve the team does this bail out and leave the form untouched. */
  private fillTeam(): void {
    const teams = this.teams() ?? [];
    const formTeamId = this.form()?.get('teamId')?.value;

    if (!teams.length || !formTeamId) {
      return;
    }

    const currentTeam = this.form()?.get('team')?.value;
    const selectedTeam =
      teams.find((team) => team.id === formTeamId) ?? (currentTeam?.id === formTeamId ? currentTeam : null);

    if (!selectedTeam) {
      return;
    }

    setTimeout(() => {
      this.form()?.patchValue({ team: selectedTeam });
      this.selectedTeam.set(selectedTeam);
    });
  }
}
